import * as React from 'react';
import {
  Button,
  Row,
  Col,
} from 'react-bootstrap';
import isEqual from 'lodash/isEqual';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import { PdfErrorBoundary } from './PdfErrorBoundary';
// Bundle the pdf.js worker locally so its version always matches the installed
// pdfjs-dist API version. A CDN URL pinned to a filename (pdf.worker.js) breaks
// whenever pdf.js is updated because the hosted filename/version can drift,
// which silently stops rendering even for valid PDFs. Webpack 5 emits this as
// an asset via new URL(..., import.meta.url).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export interface IPdfViewerProps {
  image: any;
  pageWidth: number;
  hideNavigation?: boolean;
  showPage?: string;
}
let initialState = {
  pageNumber: 1,
  pageWidth: 500,
  numPages: null,
  myDocument: null,
  error: '',
  documentKey: 0,
};
type PdfViewerState = Readonly<typeof initialState>;

export class PdfViewer extends React.Component<
  IPdfViewerProps,
  PdfViewerState
> {
  public constructor(props: IPdfViewerProps) {
    super(props);
    this.state = {
      pageNumber: (props.showPage && parseInt(props.showPage)) || 1,
      numPages: null,
      myDocument: null,
      error: '',
      pageWidth: props.pageWidth,
      documentKey: 0,
    };
  }

  shouldComponentUpdate(nextProps: IPdfViewerProps, nextState: PdfViewerState) {
    if (isEqual(nextState, this.state) && isEqual(nextProps, this.props)) {
      return false;
    }
    return true;
  }

  handlePagePrevious() {
    if (this.state.pageNumber > 1) {
      this.setState({ pageNumber: this.state.pageNumber - 1 });
    }
  }

  handlePageNext() {
    if (this.state.pageNumber < this.state.numPages)
      this.setState({ pageNumber: this.state.pageNumber + 1 });
  }

  handlePageFirst() {
    this.setState({ pageNumber: 1 });
  }

  handlePageLast() {
    this.setState({ pageNumber: this.state.numPages });
  }

  handleGrow() {
    this.setState({ pageWidth: this.state.pageWidth + 100 });
  }

  handleShrink() {
    this.setState({ pageWidth: this.state.pageWidth - 100 });
  }

  onDocumentLoadSuccess = ({ numPages }) => {
    console.log('[PdfViewer] document load success', { numPages, showPage: this.props.showPage });
    this.setState({ numPages, error: '' });
  };

  onDocumentLoadError = (error) => {
    console.error('[PdfViewer] document load error', { showPage: this.props.showPage, error });
    const message = error && error.message ? error.message : 'Document not found or error loading.';
    this.setState({ error: `Document load error: ${message}` });
  };

  onDocumentSourceError = (error) => {
    console.error('[PdfViewer] document source error', { showPage: this.props.showPage, error });
    const message = error && error.message ? error.message : 'Unable to read PDF source.';
    this.setState({ error: `Document source error: ${message}` });
  };

  onPageRenderError = (error) => {
    console.error('[PdfViewer] page render error', { showPage: this.props.showPage, error });
    const message = error && error.message ? error.message : 'Unable to render the PDF page.';
    this.setState({ error: `Page render error: ${message}` });
  };

  renderUnavailable(message: string) {
    return (
      <div
        className='pdf-viewer-unavailable text-center text-muted'
        style={{ padding: '1rem' }}
        title={message}
      >
        <i className='fas fa-file-excel fa-2x' aria-hidden='true'></i>
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>{message}</div>
      </div>
    );
  }


  componentDidUpdate(prevProps: IPdfViewerProps) {
    if (prevProps.image !== this.props.image && this.props.image) {
      this.setState((state) => ({
        error: '',
        documentKey: state.documentKey + 1,
      }));
    }
  }

  getDocumentFile() {
    const source = this.props.image;

    if (!source) {
      console.warn('[PdfViewer] no image source provided; showing unavailable indicator', {
        showPage: this.props.showPage,
      });
      return null;
    }

    if (typeof source === 'string') {
      return source;
    }

    if (source instanceof Uint8Array) {
      if (source.byteLength === 0) {
        console.warn('[PdfViewer] empty Uint8Array image payload; showing unavailable indicator', {
          showPage: this.props.showPage,
        });
        return null;
      }
      return { data: source.slice() };
    }

    if (source instanceof ArrayBuffer) {
      return { data: new Uint8Array(source.slice(0)) };
    }

    if (Array.isArray(source)) {
      return { data: new Uint8Array(source) };
    }

    if (source.url || source.range || source.data) {
      if (Array.isArray(source.data)) {
        return { ...source, data: new Uint8Array(source.data) };
      }
      if (source.data instanceof ArrayBuffer) {
        return { ...source, data: new Uint8Array(source.data.slice(0)) };
      }
      if (source.data instanceof Uint8Array) {
        return { ...source, data: source.data.slice() };
      }
      return source;
    }

    return null;
  }

  render() {
    let { pageNumber, numPages } = this.state;
    const documentFile = this.getDocumentFile();
    if (this.props.showPage && this.props.showPage.length > 0) {
      pageNumber = parseInt(this.props.showPage);
    }
    const pager = (
      <Row>
        <Col>
          <Button
            title='First'
            className='btn btn-sm btn-primary'
            onClick={() => this.handlePageFirst()}
          >
            <i className='fas fa-step-backward pointer' title='Previous'></i>
          </Button>{' '}
          <Button
            title='Previous'
            className='btn btn-sm btn-primary'
            onClick={() => this.handlePagePrevious()}
          >
            <i className='fas fa-arrow-left pointer'></i>
          </Button>
        </Col>
        <Col>
          Page {pageNumber} of {numPages}
        </Col>
        <Col>
          <i
            title='zoom down'
            className='fas fa-minus-circle fa-lg pointer'
            onClick={() => this.handleShrink()}
          ></i>
          {'   '} {this.state.pageWidth / 100} {'   '}
          <i
            title='zoom up'
            className='fas fa-plus-circle fa-lg pointer'
            onClick={() => this.handleGrow()}
          ></i>
        </Col>
        <Col>
          <Button
            title='Next'
            className='btn btn-sm btn-primary'
            onClick={() => this.handlePageNext()}
          >
            <i className='fas fa-arrow-right pointer'></i>
          </Button>{' '}
          <Button
            title='Last'
            className='btn btn-sm btn-primary'
            onClick={() => this.handlePageLast()}
          >
            <i className='fas fa-step-forward pointer'></i>
          </Button>
        </Col>
      </Row>
    );
    return (
      <React.Fragment>
        {this.props.hideNavigation || pager}
        {this.props.hideNavigation || (
          <Row>
            <Col>
              <hr />
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <div>
              {!documentFile ? (
                this.renderUnavailable('Thumbnail not available')
              ) : this.state.error.length > 0 ? (
                this.renderUnavailable(this.state.error)
              ) : (
                <PdfErrorBoundary
                  key={`pdf-boundary-${this.state.documentKey}`}
                  onError={this.onDocumentLoadError}
                >
                  <Document
                    key='image1'
                    id='image1'
                    file={documentFile}
                    onLoadSuccess={this.onDocumentLoadSuccess}
                    onLoadError={this.onDocumentLoadError}
                    onSourceError={this.onDocumentSourceError}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={this.state.pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onRenderError={this.onPageRenderError}
                    />
                  </Document>
                </PdfErrorBoundary>
              )}
            </div>
          </Col>
        </Row>
        {this.props.hideNavigation || (
          <Row>
            <Col>
              <hr />
            </Col>
          </Row>
        )}
        {this.props.hideNavigation || pager}
      </React.Fragment>
    );
  }
}
